import { Button, Checkbox, Input, Label, sonnerToast } from "@simplicity/ui";
import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../../api";
import type { ArchiveConfig, ArchiveMapping } from "../../configTypes";
import { FormBanner } from "./FormBanner";

type Banner = { type: "success" | "error"; message: string } | null;

function suggestArchiveDataset(label: string, source: string): string {
  if (!label && !source) return "";
  return `archive_${label}_${source}`;
}

function applyMappingUpdate(
  prev: ArchiveMapping[],
  index: number,
  field: keyof ArchiveMapping,
  value: string,
  label: string,
  userEdited: Set<number>,
): ArchiveMapping[] {
  const next = [...prev];
  next[index] = { ...next[index], [field]: value };
  if (field === "sourceDataset" && !userEdited.has(index)) {
    next[index].archiveDataset = suggestArchiveDataset(label, value);
  }
  return next;
}

function shiftEditedSet(prev: Set<number>, removedIndex: number): Set<number> {
  const next = new Set<number>();
  for (const v of prev) {
    if (v < removedIndex) next.add(v);
    else if (v > removedIndex) next.add(v - 1);
  }
  return next;
}

function useArchiveConfig(
  projectId: string,
  initialConfig: ArchiveConfig | null | undefined,
  onConfigChange: ((c: ArchiveConfig | null) => void) | undefined,
) {
  const preloaded = initialConfig !== undefined;
  const [enabled, setEnabled] = useState(initialConfig?.enabled ?? false);
  const [label, setLabel] = useState(initialConfig?.label ?? "");
  const [mappings, setMappings] = useState<ArchiveMapping[]>(initialConfig?.mappings ?? []);
  const [loaded, setLoaded] = useState(preloaded);
  const [exists, setExists] = useState(preloaded && initialConfig !== null);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<Banner>(null);
  const [userEdited, setUserEdited] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (preloaded) return;
    void apiFetch<ArchiveConfig>(`/api/projects/${projectId}/archive-config`)
      .then((config) => {
        setEnabled(config.enabled);
        setLabel(config.label);
        setMappings(config.mappings);
        setExists(true);
      })
      .catch(() => setExists(false))
      .finally(() => setLoaded(true));
  }, [projectId, preloaded]);

  const updateMapping = useCallback(
    (index: number, field: keyof ArchiveMapping, value: string) => {
      setMappings((prev) => applyMappingUpdate(prev, index, field, value, label, userEdited));
      setBanner(null);
      if (field === "archiveDataset") {
        setUserEdited((prev) => new Set(prev).add(index));
      }
    },
    [label, userEdited],
  );

  const handleLabelChange = useCallback(
    (newLabel: string) => {
      setLabel(newLabel);
      setBanner(null);
      setMappings((prev) =>
        prev.map((m, i) =>
          userEdited.has(i)
            ? m
            : { ...m, archiveDataset: suggestArchiveDataset(newLabel, m.sourceDataset) },
        ),
      );
    },
    [userEdited],
  );

  const addMapping = useCallback(() => {
    setMappings((prev) => [
      ...prev,
      { sourceDataset: "", archiveDataset: suggestArchiveDataset(label, "") },
    ]);
    setBanner(null);
  }, [label]);

  const removeMapping = useCallback((index: number) => {
    setMappings((prev) => prev.filter((_, i) => i !== index));
    setUserEdited((prev) => shiftEditedSet(prev, index));
    setBanner(null);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const saved = await apiFetch<ArchiveConfig>(`/api/projects/${projectId}/archive-config`, {
        method: "PUT",
        body: JSON.stringify({ enabled, label, mappings }),
      });
      setExists(true);
      onConfigChange?.(saved);
      setBanner({ type: "success", message: "Archive config saved." });
      sonnerToast.success("Archive config saved");
    } catch (err) {
      setBanner({ type: "error", message: err instanceof Error ? err.message : "Save failed" });
    } finally {
      setSaving(false);
    }
  }, [enabled, label, mappings, onConfigChange, projectId]);

  return {
    enabled,
    setEnabled,
    label,
    mappings,
    loaded,
    exists,
    saving,
    banner,
    setBanner,
    updateMapping,
    handleLabelChange,
    addMapping,
    removeMapping,
    handleSave,
  };
}

function MappingsList(props: {
  mappings: ArchiveMapping[];
  onUpdate: (index: number, field: keyof ArchiveMapping, value: string) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
}) {
  return (
    <fieldset className="mb-4 m-0 border-0 p-0">
      <legend className="form-label">Dataset mappings</legend>
      {props.mappings.length === 0 ? (
        <div className="text-sm italic text-muted-foreground">No mappings</div>
      ) : (
        <div className="space-y-2">
          {props.mappings.map((m, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                aria-label={`Source dataset ${i + 1}`}
                value={m.sourceDataset}
                onChange={(e) => props.onUpdate(i, "sourceDataset", e.target.value)}
                placeholder="Source dataset"
                className="flex-1"
              />
              <span className="text-muted-foreground">&rarr;</span>
              <Input
                aria-label={`Archive dataset ${i + 1}`}
                value={m.archiveDataset}
                onChange={(e) => props.onUpdate(i, "archiveDataset", e.target.value)}
                placeholder="Archive dataset"
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => props.onRemove(i)}
                aria-label={`Remove mapping ${i + 1}`}
              >
                &times;
              </Button>
            </div>
          ))}
        </div>
      )}
      <Button type="button" variant="outline" size="sm" className="mt-2" onClick={props.onAdd}>
        Add mapping
      </Button>
    </fieldset>
  );
}

export function ArchiveConfigEditor({
  projectId,
  initialConfig,
  onConfigChange,
}: {
  projectId: string;
  initialConfig?: ArchiveConfig | null;
  onConfigChange?: (config: ArchiveConfig | null) => void;
}) {
  const s = useArchiveConfig(projectId, initialConfig, onConfigChange);

  if (!s.loaded) {
    return (
      <div>
        <p className="mt-1 text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-4 text-sm text-muted-foreground">
        Copy source datasets to archive datasets daily
      </p>
      {s.banner && (
        <FormBanner
          type={s.banner.type}
          message={s.banner.message}
          onDismiss={() => s.setBanner(null)}
        />
      )}
      {!s.exists && s.mappings.length === 0 && (
        <div className="mb-4 text-sm italic text-muted-foreground">
          No archive config. Click below to create one.
        </div>
      )}
      <div className="mb-4 flex items-center gap-3">
        <Checkbox
          id="archive-enabled"
          checked={s.enabled}
          onCheckedChange={(v) => {
            s.setEnabled(v === true);
            s.setBanner(null);
          }}
        />
        <Label htmlFor="archive-enabled" className="form-label mb-0">
          Enabled
        </Label>
      </div>
      <div className="mb-4">
        <Label htmlFor="archive-label" className="form-label">
          School year label
        </Label>
        <Input
          id="archive-label"
          type="text"
          value={s.label}
          onChange={(e) => s.handleLabelChange(e.target.value)}
          placeholder="e.g. 2526"
          className="max-w-48"
        />
      </div>
      <MappingsList
        mappings={s.mappings}
        onUpdate={s.updateMapping}
        onRemove={s.removeMapping}
        onAdd={s.addMapping}
      />
      <div className="form-actions">
        <Button disabled={s.saving} onClick={() => void s.handleSave()}>
          {s.saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}
